import {
  Modal,
  Upload,
  Typography,
  Alert,
  Table,
  Tag,
  Space,
  Steps,
  Result,
  Divider,
  Collapse,
  Input,
  Button,
  Spin,
  Statistic,
  Card,
  Row,
  Col,
  Grid
} from "antd";
import {
  InboxOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  RedoOutlined,
  SaveOutlined,
  CheckCircleFilled,
  PlusCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import type { FmvRecord } from "./FmvTableComponent";
import { ModalHeroComponent } from "../global/ModalHeroComponent";
import type { BulkUpsertResponse } from "../../helpers/interfaces/VehiclesInterfaces";

const { Dragger } = Upload;

const modalPrimaryButtonStyle = {
  backgroundColor: "#1e293b",
  borderColor: "#1e293b",
  borderRadius: "8px",
  fontWeight: 500,
  padding: "0 24px",
};

const modalSecondaryButtonStyle = {
  borderRadius: "8px",
  fontWeight: 500,
  padding: "0 24px",
};
const { Text } = Typography;

type FmvBulkUploadModalProps = {
  open: boolean;
  onCancel: () => void;
  onConfirmUpload: (records: FmvRecord[]) => Promise<BulkUpsertResponse | null>;
  loading: boolean;
};

interface ParseError {
  row: number;
  field: string;
  message: string;
}

const REQUIRED_HEADERS = [
  "Brand",
  "Model",
  "Year",
  "Transmission",
  "Location",
  "BMTYTL",
];

const ALL_HEADERS = [
  "Brand",
  "Model",
  "Trim",
  "Year",
  "Transmission",
  "Location",
  "BMTYTL",
  "Discounted Cash Price",
  "Target Buying",
  "GDFI FMV",
  "GDFI Fin P",
  "TFS Fin P",
  "Pre-order Cash Price",
  "Reserve Fin P",
];

const headerToKey: Record<string, keyof FmvRecord> = {
  Brand: "brand",
  Model: "model",
  Year: "model_year",
  Trim: "trim",
  Transmission: "transmission",
  Location: "location",
  BMTYTL: "BMTYTL",
  "Discounted Cash Price": "discounted_cash_price",
  "Target Buying": "target_buying",
  "GDFI FMV": "gdfi_fmv",
  "GDFI Fin P": "gdfi_fin_p",
  "TFS Fin P": "tfs_fin_p",
  "Pre-order Cash Price": "pre_order_cash_price",
  "Reserve Fin P": "reserve_fin_p",
};

const PRICE_FIELDS: (keyof FmvRecord)[] = [
  "discounted_cash_price",
  "target_buying",
  "gdfi_fmv",
  "gdfi_fin_p",
  "tfs_fin_p",
  "pre_order_cash_price",
  "reserve_fin_p",
];

const parseCSV = (
  csvText: string
): { records: FmvRecord[]; errors: ParseError[] } => {
  const errors: ParseError[] = [];
  const lines = csvText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length < 2) {
    errors.push({
      row: 0,
      field: "file",
      message: "CSV file must have a header row and at least one data row.",
    });
    return { records: [], errors };
  }

  // Proper CSV line parser that handles quoted values and empty fields
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      if (inQuotes) {
        if (char === '"') {
          if (j + 1 < line.length && line[j + 1] === '"') {
            current += '"';
            j++;
          } else {
            inQuotes = false;
          }
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
    }
    result.push(current.trim());
    return result;
  };

  // Parse header
  const headers = parseCSVLine(lines[0]);

  // Validate required headers
  const missingHeaders = REQUIRED_HEADERS.filter(
    (rh) => !headers.some((h) => h.toLowerCase() === rh.toLowerCase())
  );


  if (missingHeaders.length > 0) {
    errors.push({
      row: 0,
      field: "headers",
      message: `Missing required columns: ${missingHeaders.join(", ")}`,
    });
    return { records: [], errors };
  }

  // Map header indices
  const headerMap: Record<number, keyof FmvRecord> = {};
  headers.forEach((h, i) => {
    const matchedHeader = ALL_HEADERS.find(
      (ah) => ah.toLowerCase() === h.toLowerCase()
    );
    if (matchedHeader && headerToKey[matchedHeader]) {
      headerMap[i] = headerToKey[matchedHeader];
    }
  });

  const records: FmvRecord[] = [];
  const seenBmtytl = new Set<string>();

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const record: Partial<FmvRecord> = {};

    Object.entries(headerMap).forEach(([indexStr, key]) => {
      const idx = parseInt(indexStr);
      const rawValue = values[idx] ?? "";


      if (key === "model_year") {
        const num = parseInt(rawValue);
        if (rawValue && (isNaN(num) || num < 1990 || num > 2035)) {
          errors.push({
            row: i + 1,
            field: "Year",
            message: `Invalid year "${rawValue}" — must be a number between 1990 and 2035.`,
          });
        }
        (record as any)[key] = isNaN(num) ? null : num;
      } else if (PRICE_FIELDS.includes(key)) {
        // Strip out quotes and commas first
        const cleaned = rawValue.replace(/[",]/g, "").trim();
        const num = cleaned && cleaned !== "." ? parseFloat(cleaned) : null;
        
        if (rawValue && cleaned && isNaN(num as number)) {
          errors.push({
            row: i + 1,
            field: key,
            message: `Could not parse "${rawValue}" into a valid number.`,
          });
        }
        (record as any)[key] = num;
      } else {
        (record as any)[key] = rawValue;
      }
    });

    // Validate required string fields
    const requiredStringFields: (keyof FmvRecord)[] = [
      "brand",
      "model",
      "transmission",
      "location",
      "BMTYTL",
    ];

    requiredStringFields.forEach((f) => {
      if (!record[f]) {
        errors.push({
          row: i + 1,
          field: f,
          message: `Missing required value for "${f}".`,
        });
      }
    });

    // Auto-generate BMTYTL if missing
    if (!record.BMTYTL && record.brand && record.model && record.model_year && record.transmission && record.location) {
      const trimPart = record.trim ? `_${record.trim}` : "";
      record.BMTYTL = `${record.brand}_${record.model}${trimPart}_${record.model_year}_${record.transmission}_${record.location}`.toUpperCase();
    }

    // Duplicate check
    if (record.BMTYTL) {
      if (seenBmtytl.has(record.BMTYTL as string)) {
        errors.push({
          row: i + 1,
          field: "BMTYTL",
          message: `Duplicate BMTYTL "${record.BMTYTL}" found.`,
        });
      }
      seenBmtytl.add(record.BMTYTL as string);
    }

    record.key = record.BMTYTL as string;
    records.push(record as FmvRecord);
  }

  return { records, errors };
};

export const FmvBulkUploadModal = ({
  open,
  onCancel,
  onConfirmUpload,
  loading,
}: FmvBulkUploadModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [parsedRecords, setParsedRecords] = useState<FmvRecord[]>([]);
  const [parseErrors, setParseErrors] = useState<ParseError[]>([]);
  const [fileName, setFileName] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUpsertResponse | null>(null);
  const screens = Grid.useBreakpoint();

  const resetState = () => {
    setCurrentStep(0);
    setParsedRecords([]);
    setParseErrors([]);
    setFileName("");
    setSearchText("");
    setIsParsing(false);
    setUploadResult(null);
  };

  const handleFileRead = (file: File) => {
    setFileName(file.name);
    setCurrentStep(1);
    setIsParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      // Small timeout to allow UI to render spinner in Step 1
      setTimeout(() => {
        const text = e.target?.result as string;
        const { records, errors } = parseCSV(text);
        setParsedRecords(records);
        setParseErrors(errors);
        setIsParsing(false);
      }, 500);
    };
    reader.readAsText(file);
    return false; // prevent upload
  };

  const handleConfirm = async () => {
    const result = await onConfirmUpload(parsedRecords);
    if (result) {
      setUploadResult(result);
      setCurrentStep(2);
    }
  };

  const previewColumns = [
    { title: "Row", dataIndex: "_rowNum", key: "_rowNum", width: 60, render: (_: any, __: any, idx: number) => idx + 1 },
    { title: "Brand", dataIndex: "brand", key: "brand", width: 90 },
    { title: "Model", dataIndex: "model", key: "model", width: 100 },
    { title: "Trim", dataIndex: "trim", key: "trim", width: 70 },
    { title: "Year", dataIndex: "year", key: "year", width: 70 },
    { title: "Location", dataIndex: "location", key: "location", width: 80 },
    { title: "BMTYTL", dataIndex: "BMTYTL", key: "BMTYTL", width: 150, ellipsis: true },
    { 
      title: "Disc. Cash Price", 
      dataIndex: "discounted_cash_price", 
      key: "discounted_cash_price", 
      width: 120,
      render: (val: any) => (val !== null && val !== undefined) ? `₱${val.toLocaleString()}` : '-'
    },
    { 
      title: "Target Buying", 
      dataIndex: "target_buying", 
      key: "target_buying", 
      width: 120,
      render: (val: any) => (val !== null && val !== undefined) ? `₱${val.toLocaleString()}` : '-'
    },
    { 
      title: "GDFI FMV", 
      dataIndex: "gdfi_fmv", 
      key: "gdfi_fmv", 
      width: 100,
      render: (val: any) => (val !== null && val !== undefined) ? `₱${val.toLocaleString()}` : '-'
    },
    { 
      title: "GDFI Fin P", 
      dataIndex: "gdfi_fin_p", 
      key: "gdfi_fin_p", 
      width: 100,
      render: (val: any) => (val !== null && val !== undefined) ? `₱${val.toLocaleString()}` : '-'
    },
    { 
      title: "TFS Fin P", 
      dataIndex: "tfs_fin_p", 
      key: "tfs_fin_p", 
      width: 100,
      render: (val: any) => (val !== null && val !== undefined) ? `₱${val.toLocaleString()}` : '-'
    },
    { 
      title: "Pre-order Cash", 
      dataIndex: "pre_order_cash_price", 
      key: "pre_order_cash_price", 
      width: 120,
      render: (val: any) => (val !== null && val !== undefined) ? `₱${val.toLocaleString()}` : '-'
    },
    { 
      title: "Reserve Fin P", 
      dataIndex: "reserve_fin_p", 
      key: "reserve_fin_p", 
      width: 120,
      render: (val: any) => (val !== null && val !== undefined) ? `₱${val.toLocaleString()}` : '-'
    },
  ];

  const errorColumns = [
    { title: "Row", dataIndex: "row", key: "row", width: 70 },
    { title: "Field", dataIndex: "field", key: "field", width: 140 },
    {
      title: "Issue",
      dataIndex: "message",
      key: "message",
      render: (msg: string) => <Text type="danger">{msg}</Text>,
    },
  ];

  const filteredRecords = parsedRecords.filter((record) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return Object.values(record).some(
      (val) => val !== null && val !== undefined && String(val).toLowerCase().includes(lowerSearch)
    );
  });

  return (
    <Modal
      title={
        <ModalHeroComponent
          icon={<CloudUploadOutlined />}
          title="Bulk Upload FMV Data"
          subtitle="Import vehicle prices from a CSV file"
        />
      }
      className="modal-dark-header"
      open={open}
      onCancel={() => {
        resetState();
        onCancel();
      }}
      centered
      width={screens.xs ? "100%" : "60%"}
      style={screens.xs ? { top: 0, padding: 0 } : { paddingBottom: 0 }}
      bodyStyle={{ overflowY: "auto", maxHeight: screens.xs ? "calc(100vh - 120px)" : "calc(100vh - 220px)" }}
      footer={
        <div className="modal-footer-actions">
          {currentStep === 0 && (
            <Button
              size="large"
              icon={<CloseOutlined />}
              style={modalSecondaryButtonStyle}
              onClick={() => {
                resetState();
                onCancel();
              }}
              data-testid="fmv-bulk-cancel"
            >
              Cancel
            </Button>
          )}
          {currentStep === 1 && (
            <>
              <Button
                size="large"
                icon={<RedoOutlined />}
                style={modalSecondaryButtonStyle}
                onClick={() => {
                  resetState();
                }}
                data-testid="fmv-bulk-reupload"
              >
                Re-upload
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<SaveOutlined />}
                style={modalPrimaryButtonStyle}
                disabled={parseErrors.length > 0 || parsedRecords.length === 0}
                onClick={handleConfirm}
                data-testid="fmv-bulk-confirm"
              >
                Confirm Import ({parsedRecords.length} records)
              </Button>
            </>
          )}
          {currentStep === 2 && (
            <Button
              type="primary"
              size="large"
              style={modalPrimaryButtonStyle}
              onClick={() => {
                resetState();
                onCancel();
              }}
              data-testid="fmv-bulk-close"
            >
              Close
            </Button>
          )}
        </div>
      }
    >
      <Spin spinning={loading || isParsing} tip={isParsing ? "Validating data..." : "Importing, please wait..."}>
        {screens.xs ? (
          <div style={{ marginBottom: 24, marginTop: 16, textAlign: 'center' }}>
            <Text strong style={{ color: "#1e293b", fontSize: 16 }}>
              Step {currentStep + 1} of 3: {["Upload CSV", "Review & Validate", "Complete"][currentStep]}
            </Text>
            {currentStep < 2 && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Next: {["Review & Validate", "Complete"][currentStep + 1]}
                </Text>
              </div>
            )}
          </div>
        ) : (
          <Steps
            current={currentStep}
            size="small"
            style={{ marginBottom: 24, marginTop: 16 }}
            items={[
              { title: "Upload CSV" },
              { title: "Review & Validate" },
              { title: "Complete" },
            ].map((item, index) => ({
              ...item,
              icon: currentStep > index 
                ? <CheckCircleFilled style={{ color: "#1e293b", fontSize: 24 }} /> 
                : <CheckCircleOutlined style={{ color: currentStep === index ? "#1e293b" : "#64748b", fontSize: 24 }} />
            }))}
          />
        )}

      <div className="modal-card" style={{ minHeight: "300px" }}>
        {currentStep === 0 && (
          <>
            <Collapse
              ghost
              style={{ marginBottom: 24 }}
            >
              <Collapse.Panel 
                header={<Text strong style={{ color: "#1e293b" }}>View CSV Format Guideline</Text>} 
                key="1"
              >
                <Alert
                  message="CSV Format Guide"
                  description={
                    <div>
                      <Text>
                        Your CSV file must include these <strong>required</strong>{" "}
                        columns:
                      </Text>
                      <div style={{ marginTop: 12 }}>
                        {REQUIRED_HEADERS.map((h) => (
                          <Tag color="#1e293b" key={h} style={{ marginBottom: 8 }}>
                            {h}
                          </Tag>
                        ))}
                      </div>
                      <Divider style={{ margin: "12px 0" }} />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Optional columns: Discounted Cash Price, Target Buying, GDFI
                        FMV, GDFI Fin P, TFS Fin P, Pre-order Cash Price, Reserve Fin
                        P
                      </Text>
                    </div>
                  }
                  type="info"
                  showIcon
                />
              </Collapse.Panel>
            </Collapse>
            <Dragger
              accept=".csv"
              maxCount={1}
              beforeUpload={handleFileRead}
              showUploadList={false}
              disabled={isParsing}
            >
              <p className="ant-upload-drag-icon" style={{ marginTop: "20px" }}>
                <InboxOutlined style={{ color: "#1e293b", fontSize: 48 }} />
              </p>
              <p className="ant-upload-text">
                Click or drag your CSV file here to upload
              </p>
              <p className="ant-upload-hint" style={{ marginBottom: "20px" }}>
                Supports single CSV file upload. Max recommended: 5,000 rows.
              </p>
            </Dragger>
          </>
        )}

        {currentStep === 1 && (
          <>
            <div style={{ marginBottom: 20 }}>
          <Space size={12} direction={screens.xs ? "vertical" : "horizontal"} align={screens.xs ? "start" : "center"}>
                <FileExcelOutlined style={{ fontSize: 20, color: "#1e293b" }} />
                <Text strong style={{ fontSize: "15px" }}>{fileName}</Text>
                <Tag style={{ margin: 0 }}>{parsedRecords.length} rows parsed</Tag>
                {parseErrors.length > 0 ? (
                  <Tag color="red" icon={<WarningOutlined />} style={{ borderRadius: "4px", margin: 0 }}>
                    {parseErrors.length} error{parseErrors.length > 1 ? "s" : ""}
                  </Tag>
                ) : (
                  <Tag color="green" icon={<CheckCircleOutlined />} style={{ borderRadius: "4px", margin: 0 }}>
                    All validations passed
                  </Tag>
                )}
              </Space>
            </div>

            {parseErrors.length > 0 && (
              <>
                <Alert
                  message="Please fix the following errors and re-upload"
                  type="error"
                  showIcon
                  style={{ marginBottom: 20 }}
                />
                <Table
                  columns={errorColumns}
                  dataSource={parseErrors.map((e, i) => ({ ...e, key: i }))}
                  size="small"
                  pagination={false}
                  scroll={{ y: 200 }}
                  style={{ marginBottom: 24 }}
                />
              </>
            )}

            <Divider orientation="left" plain style={{ margin: "24px 0" }}>
              Data Preview
            </Divider>
            <div style={{ marginBottom: 16 }}>
              <Input.Search
                placeholder="Search across all columns..."
                allowClear
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 300 }}
              />
            </div>
            <Table
              columns={previewColumns}
              dataSource={filteredRecords}
              size="small"
              pagination={false}
              scroll={{ x: 700, y: 350 }}
              rowKey="BMTYTL"
            />
          </>
        )}

        {currentStep === 2 && (
          <Result
              status="success"
              title="CSV Import Successful!"
              subTitle={`${parsedRecords.length} records processed.`}
              icon={<CheckCircleOutlined style={{ color: "#1e293b" }} />}
            >
              {uploadResult && (
                <Row gutter={[24, 24]} justify="center">
                    <Col xs={24} md={7}>
                      <Card 
                        bordered={true} 
                        style={{ textAlign: 'center' }}
                      >
                        <Statistic
                          title={<Text type="secondary" style={{ fontSize: '13px' }}>Records Created</Text>}
                          value={uploadResult.created}
                          prefix={<PlusCircleOutlined style={{ color: '#22c55e' }} />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} md={7}>
                      <Card 
                        bordered={true} 
                        style={{ textAlign: 'center' }}
                      >
                        <Statistic
                          title={<Text type="secondary" style={{ fontSize: '13px' }}>Records Updated</Text>}
                          value={uploadResult.updated}
                          prefix={<SyncOutlined style={{ color: '#f97316' }} />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} md={7}>
                      <Card 
                        bordered={true} 
                        style={{ textAlign: 'center' }}
                      >
                        <Statistic
                          title={<Text type="secondary" style={{ fontSize: '13px' }}>Records Skipped</Text>}
                          value={uploadResult.skipped}
                          prefix={<CloseCircleOutlined style={{ color: '#ef4444' }} />}
                        />
                      </Card>
                    </Col>
                  </Row>
              )}
            </Result>
        )}
      </div>
      </Spin>
    </Modal>
  );
};
